// src/components/digital_twin/twin_engine/assetProfile.js
// What kind of asset the twin is showing — drives the icon, the wording, which data sources are
// fetched and which tabs are relevant. Pure (no React).
import {
  FaTint, FaWater, FaFilter, FaStream, FaProjectDiagram, FaCogs, FaDatabase, FaFaucet, FaFireExtinguisher,
  FaTachometerAlt, FaSatelliteDish, FaRoad, FaBolt, FaIndustry, FaCube,
} from 'react-icons/fa';

const norm = (t) => String(t || '').trim().toLowerCase().replace(/[_-]+/g, ' ').replace(/\s+/g, ' ');
export const isWellFamily = (t) => /(^|\s)wells?$/.test(norm(t));

export const ASSET_KINDS = Object.freeze({
  drainage_well: { label: 'Drainage well', icon: FaWater, color: 'cyan', family: 'drainage', wellIntel: true, flood: true,
    about: 'Disposal well that takes storm run-off into the ground. Key risks: silting, blockage, flooding of the street around it.' },
  water_well: { label: 'Water well', icon: FaTint, color: 'blue', family: 'water', wellIntel: true, flood: true,
    about: 'Production / monitoring well. Key risks: level, casing condition, contamination, pump health.' },
  catch_basin: { label: 'Catch basin', icon: FaFilter, color: 'teal', family: 'drainage', flood: true,
    about: 'Street inlet that feeds the drainage network. Key risks: debris, silt and blocked grates causing ponding.' },
  drain: { label: 'Drain / culvert', icon: FaStream, color: 'teal', family: 'drainage', flood: true,
    about: 'Storm drain, culvert or outfall. Key risks: blockage, capacity during heavy rain, scour.' },
  pipeline: { label: 'Pipeline', icon: FaProjectDiagram, color: 'blue', family: 'water',
    about: 'Water main / pipeline. Key risks: pressure loss, leaks, bursts, age and material.' },
  pump: { label: 'Pump station', icon: FaCogs, color: 'orange', family: 'water',
    about: 'Pumping plant. Key risks: power supply, mechanical failure, flooding of electrical gear.' },
  tank: { label: 'Storage tank', icon: FaDatabase, color: 'purple', family: 'water',
    about: 'Reservoir / tank. Key risks: level, structural condition, contamination.' },
  valve: { label: 'Valve', icon: FaFaucet, color: 'gray', family: 'water', about: 'Network valve — isolation and pressure control.' },
  hydrant: { label: 'Hydrant', icon: FaFireExtinguisher, color: 'red', family: 'water', about: 'Fire hydrant — availability and pressure.' },
  meter: { label: 'Meter', icon: FaTachometerAlt, color: 'gray', family: 'water', about: 'Consumption / bulk meter.' },
  sensor: { label: 'Sensor', icon: FaSatelliteDish, color: 'purple', family: 'telemetry', about: 'IoT sensor reporting live readings.' },
  road: { label: 'Road', icon: FaRoad, color: 'yellow', family: 'road', flood: true, about: 'Road asset — surface condition and flood passability.' },
  power: { label: 'Power asset', icon: FaBolt, color: 'yellow', family: 'power', about: 'Substation / transformer / generator feeding pumps.' },
  facility: { label: 'Facility', icon: FaIndustry, color: 'purple', family: 'facility', about: 'Plant or building.' },
  generic: { label: 'Asset', icon: FaCube, color: 'gray', family: 'other', about: 'Infrastructure asset.' },
});

export function kindOf(asset) {
  if (!asset) return 'generic';
  if (asset.isSensor || asset.device_id) return 'sensor';
  if (Array.isArray(asset.path_coordinates) || asset.pipeline_material) return 'pipeline';
  const t = norm(asset.asset_type || asset.type);
  if (isWellFamily(t)) return /drain|storm|soak|disposal|recharge/.test(t) ? 'drainage_well' : 'water_well';
  if (/catch|inlet|gully/.test(t)) return 'catch_basin';
  if (/drain|culvert|outfall|swale/.test(t)) return 'drain';
  if (/pipe|main/.test(t)) return 'pipeline';
  if (/pump|lift station|booster/.test(t)) return 'pump';
  if (/tank|reservoir/.test(t)) return 'tank';
  if (/valve/.test(t)) return 'valve';
  if (/hydrant/.test(t)) return 'hydrant';
  if (/meter/.test(t)) return 'meter';
  if (/sensor|logger|rtu|gateway/.test(t)) return 'sensor';
  if (/road|street|highway|bridge/.test(t)) return 'road';
  if (/substation|transformer|generator|power|feeder/.test(t)) return 'power';
  if (/plant|building|office|facility|station/.test(t)) return 'facility';
  return 'generic';
}

export function profileOf(asset) {
  const key = kindOf(asset);
  return { key, ...ASSET_KINDS[key], typeLabel: asset?.asset_type || asset?.type || ASSET_KINDS[key].label };
}

export const parseMeta = (m) => {
  if (!m) return {};
  if (typeof m === 'object') return m;
  try { return JSON.parse(m) || {}; } catch (_) { return {}; }
};

const LABELS = {
  base_depth_m: 'Design depth (m)', well_depth_m: 'Measured depth (m)', casing_condition: 'Casing condition',
  last_inspected_date: 'Last inspected', last_inspected_by: 'Inspected by', last_cleaned_date: 'Last cleaned',
  latest_clearance_score: 'Clearance (%)', no_of_wells: 'No. of wells', no_of_catchpits: 'No. of catchpits',
  constituency: 'Constituency', source: 'Register source', surface_type: 'Surface', classification: 'Classification',
  condition_score: 'Condition score', condition_rating: 'Condition', pipeline_material: 'Material', diameter_mm: 'Diameter (mm)',
};

/** Register fields worth showing for this asset, as recorded (nothing derived). */
export function registerFields(asset) {
  if (!asset) return [];
  const meta = parseMeta(asset.metadata);
  const out = [];
  const push = (label, value) => { if (value !== null && value !== undefined && value !== '') out.push({ label, value: String(value) }); };
  push('Status', asset.status);
  push('Criticality', asset.asset_criticality);
  push('Installed', asset.install_date ? String(asset.install_date).slice(0, 10) : null);
  push('Location', asset.location_description);
  push('Zone', asset.zone_id ?? asset.constituency);
  push('Asset code', asset.asset_code);
  Object.entries(LABELS).forEach(([k, label]) => { if (meta[k] !== undefined && typeof meta[k] !== 'object') push(label, meta[k]); });
  return out;
}
